<script setup lang="ts">
import { ref, watch } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { toKebabCase } from '@/shared/utils/format'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'
import SkeletonLoader from '@/shared/components/loader/SkeletonLoader.vue'
import LabelField from '@/shared/components/label/LabelField.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import BaseDropdown from './BaseDropdown.vue'

interface Props {
  title: string
  items: any[]
  itemKey: any
  label?: boolean
  placeholder?: string
  itemText: string
  loading?: boolean
  disabled?: boolean
}

interface Emits {
  (e: 'change'): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<any>({ required: true })
const isActive = ref<boolean>(false)
const text = ref<string>('')
const name = toKebabCase(props.title)

const setDefaultValue = (): void => {
  if (props.items && props.items.length > 0) {
    const match = props.items.find((item) => item[props.itemKey] === model.value)
    if (match) {
      text.value = match[props.itemText]
    } else if (props.items.length > 0) {
      text.value = props.items[0][props.itemText]
      model.value = props.items[0][props.itemKey]
    }
  }
}

const getCurrentIcon = (): string | undefined => {
  const item = props.items.find((item) => item[props.itemKey] === model.value)
  if (item.icon) {
    return item.icon
  }
}

const updateModel = (item: any) => {
  text.value = item[props.itemText]
  model.value = item[props.itemKey]
  isActive.value = false
  emits('change')
}

watch(
  model,
  () => {
    setDefaultValue()
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="loading">
    <SkeletonLoader v-if="label" class="h-3.5 w-1/3 mb-2 mt-1.5" />
    <SkeletonLoader class="h-9.5 w-full" />
  </div>
  <div v-else class="relative" v-on-click-outside="() => (isActive = false)">
    <LabelField v-if="label" :id="name" :text="title" :disabled="disabled" />
    <div class="flex items-center border border-gray-300" :class="{ 'border-dotted': disabled }">
      <div class="absolute left-0 m-2">
        <SvgIcon class="fill-gray-700 h-5 w-5" :path="getCurrentIcon()" />
      </div>
      <input
        type="text"
        autocomplete="off"
        spellcheck="false"
        class="peer block text-sm truncate w-full pe-6 border-0 bg-transparent p-2 outline-none transition-all duration-200 ease-linear motion-reduce:transition-none enabled:cursor-pointer focus:ring-2 focus:ring-gray-700 disabled:bg-white disabled:text-gray-400"
        :class="getCurrentIcon() && 'ps-8'"
        :placeholder="placeholder"
        :id="name"
        :name="name"
        :title="title"
        :readonly="true"
        :value="text"
        @click="isActive = !isActive"
        :disabled="disabled"
      />
      <CaretIcon :active="isActive" class="absolute right-0 m-2" />
    </div>
    <BaseDropdown :open="isActive">
      <div v-if="items.length">
        <ul class="cursor-pointer">
          <li
            class="text-sm truncate hover:bg-gray-100 p-2"
            v-for="(item, index) in items"
            :key="index"
            @click="updateModel(item)"
          >
            <div class="flex items-center">
              <div v-if="item.icon" class="me-1.5">
                <SvgIcon class="fill-gray-700 h-5 w-5" :path="item.icon" />
              </div>
              <div class="truncate text-sm">{{ item[itemText] }}</div>
            </div>
          </li>
        </ul>
      </div>
    </BaseDropdown>
  </div>
</template>
